import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  BookOpen, 
  Video, 
  FileText, 
  Eye,
  Clock,
  ArrowLeft,
  Download,
  Play
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const FormazioneView = ({ visibilita, titolo = "Formazione" }) => {
  const [contenuti, setContenuti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContenuto, setSelectedContenuto] = useState(null);
  const [selectedCategoria, setSelectedCategoria] = useState('tutti');

  // Categorie per candidati
  const categorieCandidati = [
    { value: 'tutti', label: 'Tutti i Contenuti' },
    { value: 'diventa_agente', label: 'Diventa Agente' },
    { value: 'mindset_crescita', label: 'Mindset & Crescita' },
    { value: 'marketing_vendita', label: 'Marketing & Vendita' }
  ];

  // Categorie per agenzie
  const categorieAgenzie = [
    { value: 'tutti', label: 'Tutti i Contenuti' },
    { value: 'gestione_team', label: 'Gestione Team' },
    { value: 'miglioramento_colloqui', label: 'Miglioramento Colloqui' },
    { value: 'riduzione_turnover', label: 'Riduzione Turnover' },
    { value: 'motivazione_agenti', label: 'Motivazione Agenti' }
  ];

  const categorie = visibilita === 'candidati' ? categorieCandidati : categorieAgenzie;

  useEffect(() => {
    loadContenuti();
  }, [visibilita]);

  const loadContenuti = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API}/formazione/contenuti?visibilita=${visibilita}&pubblicato=true`
      );
      if (!response.ok) throw new Error('Errore caricamento');
      const data = await response.json();
      setContenuti(data);
    } catch (error) {
      console.error('Error loading contents:', error);
    } finally {
      setLoading(false);
    }
  };

  const contenutiFiltrati = selectedCategoria === 'tutti' 
    ? contenuti 
    : contenuti.filter(c => c.categoria === selectedCategoria);

  const getTipoIcon = (tipo) => {
    const icons = {
      articolo: FileText,
      video: Video,
      pdf: BookOpen
    };
    const Icon = icons[tipo] || FileText;
    return <Icon className="w-5 h-5" />;
  };

  const getTipoBadge = (tipo) => {
    const colors = {
      articolo: 'bg-blue-100 text-blue-700',
      video: 'bg-purple-100 text-purple-700',
      pdf: 'bg-green-100 text-green-700'
    };
    return colors[tipo] || colors.articolo;
  };

  const getTipoLabel = (tipo) => {
    const labels = {
      articolo: 'Articolo',
      video: 'Video',
      pdf: 'Guida PDF'
    };
    return labels[tipo] || 'Contenuto';
  };

  if (selectedContenuto) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <Button
          onClick={() => setSelectedContenuto(null)}
          variant="outline"
          className="rounded-xl"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna alla lista
        </Button>

        {/* Contenuto Dettaglio */}
        <Card className="border-0 shadow-lg rounded-2xl">
          <CardHeader>
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <Badge className={`${getTipoBadge(selectedContenuto.tipo)} border-0 mb-3`}>
                  {getTipoIcon(selectedContenuto.tipo)}
                  <span className="ml-1">{getTipoLabel(selectedContenuto.tipo)}</span>
                </Badge>
                <CardTitle className="text-2xl mb-2">{selectedContenuto.titolo}</CardTitle>
                <p className="text-gray-600">{selectedContenuto.descrizione_breve}</p>
              </div>
            </div>

            {/* Immagine */}
            {selectedContenuto.immagine_url && (
              <div className="rounded-xl overflow-hidden mb-6">
                <img 
                  src={selectedContenuto.immagine_url} 
                  alt={selectedContenuto.titolo}
                  className="w-full h-64 object-cover"
                />
              </div>
            )}
          </CardHeader>

          <CardContent>
            {/* Video */}
            {selectedContenuto.tipo === 'video' && selectedContenuto.video_url && (
              <div className="mb-6">
                <div className="aspect-video rounded-xl overflow-hidden bg-black">
                  <iframe
                    src={selectedContenuto.video_url}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {/* PDF Download */}
            {selectedContenuto.tipo === 'pdf' && selectedContenuto.pdf_url && (
              <div className="mb-6">
                <Button
                  onClick={() => window.open(selectedContenuto.pdf_url, '_blank')}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl py-6"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Scarica Guida PDF
                </Button>
              </div>
            )}

            {/* Testo Completo */}
            {selectedContenuto.testo_completo && (
              <div className="prose max-w-none">
                <div 
                  className="text-gray-700 leading-relaxed whitespace-pre-line"
                  dangerouslySetInnerHTML={{ __html: selectedContenuto.testo_completo.replace(/\n/g, '<br/>') }}
                />
              </div>
            )}

            {/* Tags */}
            {selectedContenuto.tags && selectedContenuto.tags.length > 0 && (
              <div className="flex gap-2 flex-wrap mt-6 pt-6 border-t">
                {selectedContenuto.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Metadata */}
            <div className="flex items-center gap-4 mt-6 pt-6 border-t text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                <span>{selectedContenuto.visualizzazioni || 0} visualizzazioni</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>
                  {new Date(selectedContenuto.data_pubblicazione).toLocaleDateString('it-IT', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-0 shadow-lg rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-600" />
            {titolo}
          </CardTitle>
          <p className="text-gray-600 mt-2">
            {visibilita === 'candidati' 
              ? 'Accedi a contenuti esclusivi per accelerare la tua crescita professionale'
              : 'Risorse e strumenti per migliorare la gestione del tuo team'}
          </p>
        </CardHeader>
      </Card>

      {/* Filtri Categoria */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categorie.map((cat) => (
          <Button
            key={cat.value}
            onClick={() => setSelectedCategoria(cat.value)}
            variant={selectedCategoria === cat.value ? 'default' : 'outline'}
            className={`rounded-xl whitespace-nowrap ${
              selectedCategoria === cat.value
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                : ''
            }`}
          >
            {cat.label}
          </Button>
        ))}
      </div>

      {/* Lista Contenuti */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento contenuti...</p>
        </div>
      ) : contenutiFiltrati.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-2xl">
          <CardContent className="p-12 text-center">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nessun contenuto disponibile in questa categoria</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {contenutiFiltrati.map((contenuto) => (
            <Card 
              key={contenuto.id}
              className="border-0 shadow-lg rounded-2xl hover:shadow-xl transition-shadow cursor-pointer overflow-hidden"
              onClick={() => setSelectedContenuto(contenuto)}
            >
              {/* Immagine */}
              {contenuto.immagine_url && (
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={contenuto.immagine_url} 
                    alt={contenuto.titolo}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3">
                    <Badge className={`${getTipoBadge(contenuto.tipo)} border-0 shadow-lg`}>
                      {getTipoIcon(contenuto.tipo)}
                      <span className="ml-1">{getTipoLabel(contenuto.tipo)}</span>
                    </Badge>
                  </div>
                </div>
              )}

              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-2 line-clamp-2">
                  {contenuto.titolo}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {contenuto.descrizione_breve}
                </p>

                {/* Tags */}
                {contenuto.tags && contenuto.tags.length > 0 && (
                  <div className="flex gap-1 flex-wrap mb-4">
                    {contenuto.tags.slice(0, 3).map((tag, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* CTA */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <Eye className="w-4 h-4" />
                    <span>{contenuto.visualizzazioni || 0}</span>
                  </div>
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl"
                  >
                    {contenuto.tipo === 'video' ? (
                      <>
                        <Play className="w-4 h-4 mr-1" />
                        Guarda
                      </>
                    ) : contenuto.tipo === 'pdf' ? (
                      <>
                        <Download className="w-4 h-4 mr-1" />
                        Scarica
                      </>
                    ) : (
                      <>
                        Leggi
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default FormazioneView;
